import type { ApiResponse, Category } from '@packages/shared-types';

import { apiClient } from '@/services/apiClient';

export type CreateCategoryPayload = {
  name: string;
  tenantId?: string;
  folderId?: string | null;
  code?: string;
};

export type UpdateCategoryPayload = {
  code?: string;
  name?: string;
  folderId?: string | null;
};

export interface ListCategoriesPaginatedParams {
  page: number;
  limit: number;
}

export interface ListCategoriesPaginatedResult {
  data: Category[];
  total: number;
  page: number;
  limit: number;
}

export async function listCategoriesPaginated(
  params: ListCategoriesPaginatedParams,
): Promise<ListCategoriesPaginatedResult> {
  const { page, limit } = params;
  const response = await apiClient.get<ApiResponse<ListCategoriesPaginatedResult>>(
    '/api/categories',
    { params: { page, limit } },
  );
  const payload = response.data.data;
  return {
    data: payload.data,
    total: payload.total,
    page: payload.page,
    limit: payload.limit,
  };
}

export async function listCategories(): Promise<Category[]> {
  const response = await apiClient.get<ApiResponse<Category[]>>('/api/categories');
  return response.data.data;
}

export async function getCategoryById(id: string): Promise<Category> {
  const response = await apiClient.get<ApiResponse<Category>>(`/api/categories/${id}`);
  return response.data.data;
}

export type NextProductSuggestion = {
  suggestedName: string;
  suggestedSku: string;
  nextSequenceNumber: number;
};

export async function getNextProductSuggestion(
  categoryId: string,
): Promise<NextProductSuggestion> {
  const response = await apiClient.get<ApiResponse<NextProductSuggestion>>(
    `/api/categories/${categoryId}/next-product-suggestion`,
  );
  return response.data.data;
}

export async function getNextCategoryCode(): Promise<{ code: string }> {
  const response = await apiClient.get<ApiResponse<{ code: string }>>(
    '/api/categories/next-code',
  );
  return response.data.data;
}

export async function createCategory(payload: CreateCategoryPayload): Promise<Category> {
  const response = await apiClient.post<ApiResponse<Category>>('/api/categories', payload);
  return response.data.data;
}

export async function updateCategory(
  id: string,
  payload: UpdateCategoryPayload,
): Promise<Category> {
  const response = await apiClient.put<ApiResponse<Category>>(
    `/api/categories/${id}`,
    payload,
  );
  return response.data.data;
}

export async function deleteCategory(id: string): Promise<void> {
  await apiClient.delete<ApiResponse<void>>(`/api/categories/${id}`);
}
